import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, CalendarDays, Sparkles, CheckCheck, Bot, BarChart3, Palette, Users, LogOut,
  FileEdit, Newspaper, Globe, ChevronsUpDown, Check, Plus, Shield, UserPlus, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { apiError, switchWorkspace } from "../lib/auth";
import { useSession } from "../lib/session";
import { Mark } from "../marketing/Brand";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "composer", label: "Create Post", icon: Sparkles },
  { id: "news", label: "News & Feeds", icon: Newspaper },
  { id: "blog", label: "Blog", icon: Globe },
  { id: "drafts", label: "Drafts", icon: FileEdit },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "approvals", label: "Approvals", icon: CheckCheck },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const WORKSPACE = [
  { id: "autopilot", label: "Autopilot", icon: Bot },
  { id: "brands", label: "Brands", icon: Palette },
  { id: "accounts", label: "Accounts", icon: Users },
  { id: "team", label: "Team", icon: UserPlus },
];

const NavBtn = ({ item, active, onChange, pendingCount }) => {
  const Icon = item.icon;
  const is = active === item.id;
  return (
    <button
      data-testid={`nav-${item.id}`}
      onClick={() => onChange(item.id)}
      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
        is ? "bg-indigo-500/12 text-white" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
      }`}
    >
      {is && <motion.span layoutId="active-pill" className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-indigo-400" />}
      <Icon className={`w-[18px] h-[18px] ${is ? "text-indigo-300" : ""}`} />
      <span>{item.label}</span>
      {item.id === "approvals" && pendingCount > 0 && (
        <span className="ml-auto text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300" data-testid="pending-badge">{pendingCount}</span>
      )}
    </button>
  );
};

const PLAN_TONE = {
  free: "bg-slate-500/15 text-slate-300", pro: "bg-indigo-500/20 text-indigo-200",
  agency: "bg-fuchsia-500/20 text-fuchsia-200", unlimited: "bg-emerald-500/15 text-emerald-200",
};

/* Which workspace you are acting in, and the way to move between them. The
   token carries the workspace, so switching reloads the app cleanly rather
   than trying to patch every open view's state. */
const WorkspaceSwitcher = () => {
  const { me, isAdmin } = useSession();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const current = me.workspace;

  const go = async (id) => {
    if (id === current.id) return;
    try {
      await switchWorkspace(id);
      window.location.assign("/app");
    } catch (err) {
      toast.error(apiError(err, "Could not switch workspace"));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", { name });
      await switchWorkspace(data.id);
      window.location.assign("/app?welcome=1");
    } catch (err) {
      toast.error(apiError(err, "Could not create the workspace"));
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="workspace-switcher"
            className="w-full flex items-center gap-2.5 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-800/50 px-3 py-2.5 text-left transition-colors">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
              {(current.name || "W")[0].toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-slate-100 truncate">{current.name}</span>
              <span className={`inline-block mt-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-px rounded ${PLAN_TONE[current.plan] || PLAN_TONE.free}`}>
                {me.plan?.label || current.plan}
              </span>
            </span>
            <ChevronsUpDown className="w-4 h-4 text-slate-500 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-60 bg-slate-900 border-slate-800 text-slate-200" align="start">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Workspaces</DropdownMenuLabel>
          {(me.workspaces || []).map((w) => (
            <DropdownMenuItem key={w.id} onClick={() => go(w.id)} className="cursor-pointer focus:bg-slate-800">
              <span className="truncate flex-1">{w.name}</span>
              {w.id === current.id && <Check className="w-4 h-4 text-indigo-300" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-slate-800" />
          <DropdownMenuItem onClick={() => setCreating(true)} className="cursor-pointer focus:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> New workspace
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-slate-800 text-fuchsia-200">
              <Shield className="w-4 h-4 mr-2" /> Admin console
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">New workspace</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400 -mt-2">A separate space for another brand or client - its own posts, brands, accounts and team.</p>
          <form onSubmit={create} className="space-y-3">
            <input autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="Client or brand name"
              className="w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-indigo-500/60" />
            <button disabled={busy} className="w-full py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create and switch"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* This month's usage against the plan, so a limit is never a surprise. */
const UsageMeter = () => {
  const { me } = useSession();
  if (!me?.plan || !me?.usage) return null;
  const rows = [["Posts", me.usage.posts, me.plan.posts_per_month], ["Images", me.usage.images, me.plan.images_per_month]];
  return (
    <div className="glass rounded-xl p-3.5 space-y-3" data-testid="usage-meter">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">This month</p>
      {rows.map(([label, used, cap]) => {
        const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
        // PL-01/02: a warning at 80%, and at 100% a plain statement of what
        // stops - never a surprise bill.
        const tone = pct >= 100 ? "bg-[#F87171]" : pct >= 80 ? "bg-[#F472B6]" : "bg-[#A5B4FC]";
        return (
          <div key={label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{label}</span>
              <span className="text-slate-300 font-medium">{used}{cap ? ` / ${cap}` : ""}</span>
            </div>
            {cap ? (
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
              </div>
            ) : (
              <p className="text-[10px] text-slate-600">Unlimited</p>
            )}
            {cap && pct >= 100 ? (
              <p className="mt-1 text-[10px] text-[#FCA5A5]">Limit reached: no new {label.toLowerCase()} until next month. Approved posts still publish.</p>
            ) : cap && pct >= 80 ? (
              <p className="mt-1 text-[10px] text-[#F9A8D4]">{100 - pct}% left this month.</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export const Sidebar = ({ active, onChange, pendingCount = 0, onSignOut }) => (
  <aside className="hidden md:flex flex-col w-64 shrink-0 h-screen sticky top-0 bg-ac-bg border-r border-ac-border px-4 py-6 overflow-y-auto">
    <div className="flex items-center gap-2.5 px-2 mb-6">
      <Mark className="w-8 h-8" />
      <p className="font-heading font-extrabold text-[17px] tracking-tight text-slate-50">
        Auto<span className="text-gradient">Cruise</span>
      </p>
    </div>

    <div className="mb-6"><WorkspaceSwitcher /></div>

    <nav className="flex flex-col gap-1.5" data-testid="sidebar-nav">
      {NAV.map((item) => <NavBtn key={item.id} item={item} active={active} onChange={onChange} pendingCount={pendingCount} />)}
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 px-3 mt-5 mb-1">Workspace</p>
      {WORKSPACE.map((item) => <NavBtn key={item.id} item={item} active={active} onChange={onChange} />)}
    </nav>

    <div className="mt-auto pt-6 flex flex-col gap-2.5">
      <UsageMeter />
      <button
        data-testid="sign-out"
        onClick={onSignOut}
        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors duration-200"
      >
        <LogOut className="w-[18px] h-[18px]" />
        <span>Sign out</span>
      </button>
    </div>
  </aside>
);

export const MobileNav = ({ active, onChange, onSignOut }) => (
  <div className="md:hidden sticky top-0 z-40 bg-ac-bg border-b border-ac-border flex items-center overflow-x-auto gap-1 px-3 py-2">
    <div className="shrink-0 w-48 mr-1"><WorkspaceSwitcher /></div>
    {[...NAV, ...WORKSPACE].map((item) => (
      <button key={item.id} data-testid={`mnav-${item.id}`} onClick={() => onChange(item.id)}
        className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold ${active === item.id ? "bg-indigo-500/15 text-indigo-300" : "text-slate-400"}`}>
        {item.label}
      </button>
    ))}
    <button data-testid="mnav-sign-out" onClick={onSignOut} aria-label="Sign out"
      className="ml-auto shrink-0 p-1.5 rounded-lg text-slate-500 hover:text-rose-300">
      <LogOut className="w-4 h-4" />
    </button>
  </div>
);
